/**
 * PLAT-364 (Shell 2a): the shell navigation as data, for the Workspace AppShell
 * wrapper (components/AppShell.tsx) to hand Foundation's AppShell as `NavNode`s.
 * The admin tree is the old admin sidebar's, unchanged: labels, hrefs, order,
 * icons and `ownerOnly` filtering, and its search index with the same keywords.
 * PLAT-365 (Shell 3) adds the user tree: TopNav's user-mode links, unchanged.
 *
 * Labels are user-facing copy and URLs are implementation (Ean, 2026-06-10):
 * "People" stays at /admin/people. Don't align them.
 *
 * Every admin href stays inside /admin/* (scripts/check-policies.sh check 5);
 * shellNav.test.tsx and shellNavigation.test.tsx pin that.
 */

import type { NavNode } from '@olytic/foundation';
import type { ReactNode } from 'react';
import type { SearchResultKind } from '../lib/api/search.ts';
import type { Role } from '../lib/types.ts';
import type { ObjectKind } from './ObjectIcon.tsx';

export interface ShellNavEntry {
  label: string;
  href: string;
  /** The ObjectIcon kind; Foundation shows it on top-level rows only. */
  iconKind: ObjectKind;
  /** Active only on this exact path, as react-router's NavLink `end`. */
  end?: boolean;
  ownerOnly?: boolean;
  /** Shown only while the server reports artifacts enabled (PLAT-213, /api/me artifacts_enabled). */
  artifactsOnly?: boolean;
  /** Shows the pending invitations plus open requests count. */
  counted?: boolean;
}

export interface ShellNavGroup {
  /** The group's key in Foundation's AppShell. */
  group: string;
  label: string;
  iconKind: ObjectKind;
  children: ShellNavEntry[];
}

export type ShellNavNode = ShellNavEntry | ShellNavGroup;

const isGroup = (n: ShellNavNode): n is ShellNavGroup => 'group' in n;

/** The admin sidebar's 6-item IA (Push 5-post-smoke-32), in order. No entry is ownerOnly today. */
export const ADMIN_NAV: ShellNavNode[] = [
  { label: 'Home', href: '/admin', iconKind: 'home', end: true },
  { label: 'Workspace', href: '/admin/workspace', iconKind: 'workspace', end: true },
  { label: 'People', href: '/admin/people', iconKind: 'people', counted: true },
  {
    group: 'capabilities',
    label: 'Capabilities',
    iconKind: 'capabilities',
    // D-167: Models → Connectors → Prompts → Library → Market
    children: [
      { label: 'Models', href: '/admin/models', iconKind: 'models' },
      { label: 'Connectors', href: '/admin/connectors', iconKind: 'connectors' },
      { label: 'Prompts', href: '/admin/prompts', iconKind: 'prompts' },
      { label: 'Library', href: '/admin/library', iconKind: 'library' },
      { label: 'Market', href: '/admin/market', iconKind: 'catalog' },
    ],
  },
  {
    group: 'permissions',
    label: 'Permissions',
    iconKind: 'permission-sets',
    children: [
      {
        label: 'Permission sets',
        href: '/admin/permission-sets',
        iconKind: 'permission-set-shield',
      },
      { label: 'Assignments', href: '/admin/assignments', iconKind: 'assignment-link' },
    ],
  },
  {
    group: 'analytics',
    label: 'Analytics',
    iconKind: 'analytics',
    children: [
      { label: 'Overview', href: '/admin/analytics', iconKind: 'dashboard', end: true },
      { label: 'Reports', href: '/admin/analytics/reports', iconKind: 'token-debit' },
      { label: 'Activity', href: '/admin/analytics/activity', iconKind: 'pulse' },
    ],
  },
];

/**
 * TopNav's user-mode links, in order (IA per OC 2026-06-09, D-167 inside
 * Capabilities). Activity stays out of the user lane (Ean 2026-06-09, D-153).
 */
export const USER_NAV: ShellNavNode[] = [
  { label: 'Chat', href: '/chat', iconKind: 'chat' },
  { label: 'Home', href: '/home', iconKind: 'home', end: true },
  { label: 'Artifacts', href: '/artifacts', iconKind: 'document', artifactsOnly: true },
  {
    group: 'capabilities',
    label: 'Capabilities',
    iconKind: 'capabilities',
    children: [
      { label: 'Models', href: '/models', iconKind: 'models' },
      { label: 'Connectors', href: '/connectors', iconKind: 'connectors' },
      { label: 'Prompts', href: '/prompts', iconKind: 'prompts' },
      { label: 'Library', href: '/library', iconKind: 'library' },
    ],
  },
  { label: 'Market', href: '/market', iconKind: 'catalog' },
];

export interface ShellNavSearchEntry {
  label: string;
  href: string;
  keywords: string[];
  parent?: string;
  ownerOnly?: boolean;
}

/**
 * The admin sidebar's search (Push #17): wayfinding over the nav, its page
 * tabs and aliases. Data search is the top bar's search palette (SearchPalette).
 */
// biome-ignore format: one entry per line reads as the table it is.
export const ADMIN_NAV_SEARCH: ShellNavSearchEntry[] = [
  { label: 'Home', href: '/admin', keywords: ['home', 'overview', 'dashboard'] },
  { label: 'General', href: '/admin/workspace', keywords: ['workspace', 'general', 'overview', 'settings', 'preferences', 'name', 'timezone', 'default model', 'memory'], parent: 'Workspace' },
  { label: 'Usage', href: '/admin/workspace/usage', keywords: ['usage', 'team pot', 'credits', 'balance', 'monthly cap', 'monthly limit', 'auto-reload', 'auto reload', 'buy credits', 'per-user', 'spend cap', 'wallet', 'personal cap', 'tags'], parent: 'Workspace' },
  { label: 'Billing', href: '/admin/workspace/billing', keywords: ['billing', 'invoices', 'payment method', 'card', 'saved card', 'credit card', 'receipts', 'stripe'], parent: 'Workspace' },
  { label: 'Notifications', href: '/admin/workspace/notifications', keywords: ['notifications', 'recipients', 'alerts', 'email'], parent: 'Workspace' },
  { label: 'People', href: '/admin/people', keywords: ['people', 'users', 'team', 'members', 'invitations', 'approvals', 'deactivated', 'inactive'] },
  { label: 'Users', href: '/admin/people', keywords: ['users', 'everyone', 'roster', 'all users', 'members', 'team'], parent: 'People' },
  { label: 'Invitations', href: '/admin/people?tab=invitations', keywords: ['invitations', 'invites', 'invite'], parent: 'People' },
  // "Requests" matches the on-page tab; ?tab=approvals still works for old bookmarks.
  { label: 'Requests', href: '/admin/people?tab=requests', keywords: ['requests', 'approvals', 'awaiting approval', 'credit approvals', 'pending approvals', 'access requests', 'help', 'support'], parent: 'People' },
  { label: 'Inactive users', href: '/admin/people?status=inactive', keywords: ['deactivated', 'inactive', 'removed', 'frozen'], parent: 'People' },
  { label: 'Models', href: '/admin/models', keywords: ['models', 'llm', 'ai'], parent: 'Capabilities' },
  { label: 'Connectors', href: '/admin/connectors', keywords: ['connectors', 'apps', 'integrations'], parent: 'Capabilities' },
  { label: 'Prompts', href: '/admin/prompts', keywords: ['prompts', 'knowledge', 'templates'], parent: 'Capabilities' },
  { label: 'Library', href: '/admin/library', keywords: ['library', 'documents', 'knowledge base', 'files'], parent: 'Capabilities' },
  { label: 'Market', href: '/admin/market', keywords: ['market', 'marketplace', 'catalog', 'browse'], parent: 'Capabilities' },
  { label: 'Permission sets', href: '/admin/permission-sets', keywords: ['permission sets', 'roles', 'permissions', 'bundles'], parent: 'Permissions' },
  { label: 'Assignments', href: '/admin/assignments', keywords: ['assignments', 'grants', 'permissions'], parent: 'Permissions' },
  // Legacy 'spend' and 'insights' route to their new homes for muscle memory.
  { label: 'Overview', href: '/admin/analytics', keywords: ['analytics', 'overview', 'summary', 'dashboard', 'insights', 'callouts', 'optimization', 'top users', 'top models'], parent: 'Analytics' },
  { label: 'Reports', href: '/admin/analytics/reports', keywords: ['reports', 'spend', 'cost', 'spending', 'credits', 'by user', 'by model', 'by capability', 'by connector', 'by prompt', 'by tag', 'by day', 'pivot', 'drilldown', 'csv', 'export'], parent: 'Analytics' },
  { label: 'Activity', href: '/admin/analytics/activity', keywords: ['activity', 'runs', 'dispatches', 'ai calls', 'audit', 'admin actions', 'event log', 'compliance'], parent: 'Analytics' },
];

const visibleTo =
  (role: Role, artifacts = false) =>
  (e: { ownerOnly?: boolean; artifactsOnly?: boolean }) =>
    !(e.ownerOnly === true && role !== 'owner') && !(e.artifactsOnly === true && !artifacts);

/**
 * The tree as `role` sees it: ownerOnly entries for the owner only,
 * artifactsOnly entries only when `artifacts` is on, empty groups dropped.
 */
export function navFor(tree: ShellNavNode[], role: Role, artifacts = false): ShellNavNode[] {
  const visible = visibleTo(role, artifacts);
  return tree.flatMap((node): ShellNavNode[] => {
    if (!isGroup(node)) return visible(node) ? [node] : [];
    const children = node.children.filter(visible);
    return children.length > 0 ? [{ ...node, children }] : [];
  });
}

/** Whether `pathname` is at an entry, as react-router's NavLink matches it. */
export function isAt(entry: ShellNavEntry, pathname: string): boolean {
  if (pathname === entry.href) return true;
  return entry.end !== true && pathname.startsWith(`${entry.href}/`);
}

/** The href (the AppShell key) of the entry `pathname` is at, if any. */
export function activeNavKey(tree: ShellNavNode[], pathname: string): string | undefined {
  const leaves = tree.flatMap((n) => (isGroup(n) ? n.children : [n]));
  return leaves.find((e) => isAt(e, pathname))?.href;
}

/** The groups holding the entry `pathname` is at: open on first render, as the old sidebar did. */
export function openNavGroups(tree: ShellNavNode[], pathname: string): string[] {
  return tree.flatMap((n) =>
    isGroup(n) && n.children.some((c) => isAt(c, pathname)) ? [n.group] : [],
  );
}

/** Search hits for `query`: label or keyword contains it, at most 12, as the old sidebar listed them. */
export function searchNav(
  index: ShellNavSearchEntry[],
  role: Role,
  query: string,
): ShellNavSearchEntry[] {
  const term = query.trim().toLowerCase();
  if (term === '') return [];
  return index
    .filter(visibleTo(role))
    .filter(
      (e) =>
        e.label.toLowerCase().includes(term) ||
        e.keywords.some((k) => k.toLowerCase().includes(term)),
    )
    .slice(0, 12);
}

/** Foundation `NavNode`s; `icon` draws an ObjectIcon kind, `count` is the People count. */
export function toNavNodes(
  tree: ShellNavNode[],
  icon: (kind: ObjectKind) => ReactNode,
  count?: number,
): NavNode[] {
  const leaf = (e: ShellNavEntry): NavNode => ({
    key: e.href,
    label: e.label,
    href: e.href,
    icon: icon(e.iconKind),
    ...(e.counted === true && count !== undefined && count > 0 ? { count } : {}),
  });
  return tree.map((n) =>
    isGroup(n)
      ? { key: n.group, label: n.label, icon: icon(n.iconKind), children: n.children.map(leaf) }
      : leaf(n),
  );
}

/** Search hits as nav rows, labelled "Parent → Label" as the old sidebar did. */
export function hitNodes(hits: ShellNavSearchEntry[]): NavNode[] {
  return hits.map((h) => ({
    key: `search:${h.href}`,
    label: h.parent !== undefined ? `${h.parent} → ${h.label}` : h.label,
    href: h.href,
  }));
}

/**
 * The search palette's Pages and Actions (Push 4-1aj), moved unchanged from
 * GlobalSearch in PLAT-366 (Shell 4). Pages navigate; Actions fire a callback
 * the palette provides (Buy credits, sign out, the admin toggle). Records
 * (connectors, prompts, users) come from `/api/search`, not from here.
 *
 * The registry is small on purpose: each entry is either the "how do I get to
 * X" a first-day teammate would ask, or a verb that reads as a first-class
 * action in the product.
 */
export interface PaletteCommand {
  id: string;
  title: string;
  subtitle: string;
  kind: SearchResultKind;
  groupLabel: 'Pages' | 'Actions';
  /** Roles that see this command. Everyone if omitted. */
  roles?: readonly ('owner' | 'admin' | 'user')[];
  /** Navigation target (mutually exclusive with `firesEvent`). */
  to?: string;
  /**
   * Fire mode for Actions: 'buy_credits' opens BuyCreditsProvider modal,
   * 'sign_out' fires the signOut() flow, 'toggle_admin' jumps between
   * /home and /admin. Undefined for Page rows (they navigate via `to`).
   * Push 5-post-smoke-28 dropped the 'keyboard_help' action row per
   * app-wide "kill all keyboard shortcuts" directive.
   */
  action?: 'buy_credits' | 'sign_out' | 'toggle_admin';
  keywords?: readonly string[];
}

// biome-ignore format: one entry per line reads as the table it is.
export const PALETTE_COMMANDS: readonly PaletteCommand[] = [
  // --- User-mode pages -------------------------------------------------
  { id: 'page-home', title: 'Home', subtitle: 'Workspace overview', kind: 'connector', groupLabel: 'Pages', to: '/home', keywords: ['dashboard', 'orient', 'getting started'] },
  { id: 'page-chat', title: 'Chat', subtitle: 'Ask your AI anything', kind: 'chat_session', groupLabel: 'Pages', to: '/chat', keywords: ['message', 'ask', 'ai'] },
  { id: 'page-models', title: 'Models', subtitle: 'Your assigned models', kind: 'model', groupLabel: 'Pages', to: '/models', keywords: ['llm', 'gpt', 'claude'] },
  { id: 'page-connectors', title: 'Connectors', subtitle: 'Your installed connectors', kind: 'connector', groupLabel: 'Pages', to: '/connectors', keywords: ['tools', 'apps', 'integrations'] },
  { id: 'page-prompts', title: 'Prompts', subtitle: 'Your prompt library', kind: 'prompt', groupLabel: 'Pages', to: '/prompts', keywords: ['templates', 'knowledge'] },
  { id: 'page-library', title: 'Library', subtitle: 'Documents + folders', kind: 'document', groupLabel: 'Pages', to: '/library', keywords: ['documents', 'files', 'knowledge base'] },
  { id: 'page-market', title: 'Market', subtitle: 'Browse everything Olytic offers', kind: 'catalog_connector', groupLabel: 'Pages', to: '/market', keywords: ['catalog', 'install', 'store'] },
  { id: 'page-activity', title: 'Activity', subtitle: 'Your dispatches', kind: 'activity', groupLabel: 'Pages', to: '/activity', keywords: ['history', 'audit', 'runs'] },
  { id: 'page-settings', title: 'Settings', subtitle: 'Profile + preferences', kind: 'connector', groupLabel: 'Pages', to: '/settings', keywords: ['profile', 'preferences', 'account'] },
  { id: 'page-legal', title: 'Legal & policies', subtitle: 'Terms, privacy, acceptable use', kind: 'connector', groupLabel: 'Pages', to: '/legal', keywords: ['terms', 'privacy', 'policy'] },

  // --- Admin-mode pages (role-filtered at render) ----------------------
  { id: 'page-admin', title: 'Admin home', subtitle: 'Team overview', kind: 'activity', groupLabel: 'Pages', to: '/admin', roles: ['owner', 'admin'], keywords: ['dashboard', 'overview'] },
  { id: 'page-admin-people', title: 'People', subtitle: 'Team, invitations, approvals', kind: 'user', groupLabel: 'Pages', to: '/admin/people', roles: ['owner', 'admin'], keywords: ['users', 'members', 'invite', 'roster'] },
  { id: 'page-admin-permission-sets', title: 'Permission sets', subtitle: 'Bundle capabilities for teammates', kind: 'permission_set', groupLabel: 'Pages', to: '/admin/permission-sets', roles: ['owner', 'admin'], keywords: ['ps', 'roles', 'access', 'assignments'] },
  { id: 'page-admin-models', title: 'Models (admin)', subtitle: 'Manage models workspace-wide', kind: 'model', groupLabel: 'Pages', to: '/admin/models', roles: ['owner', 'admin'], keywords: ['llm', 'admin'] },
  { id: 'page-admin-connectors', title: 'Connectors (admin)', subtitle: 'Manage connectors workspace-wide', kind: 'connector', groupLabel: 'Pages', to: '/admin/connectors', roles: ['owner', 'admin'], keywords: ['tools', 'admin'] },
  { id: 'page-admin-prompts', title: 'Prompts (admin)', subtitle: 'Manage prompts workspace-wide', kind: 'prompt', groupLabel: 'Pages', to: '/admin/prompts', roles: ['owner', 'admin'], keywords: ['knowledge', 'admin'] },
  { id: 'page-admin-market', title: 'Market (admin)', subtitle: 'Install for the workspace', kind: 'catalog_connector', groupLabel: 'Pages', to: '/admin/market', roles: ['owner', 'admin'], keywords: ['catalog', 'install'] },
  // Sub-agent WWWWW (2026-07-31) — label + URL aligned to the on-page
  // tab ("Requests"). People.tsx normalizePeople still accepts
  // ?tab=approvals for legacy bookmarks. Legacy keywords preserved so
  // muscle-memory search keeps working.
  { id: 'page-admin-approvals', title: 'Requests', subtitle: 'Access + support requests awaiting you', kind: 'request', groupLabel: 'Pages', to: '/admin/people?tab=requests', roles: ['owner', 'admin'], keywords: ['inbox', 'requests', 'approvals', 'awaiting', 'access', 'support'] },
  // Sub-agent TTTTT (2026-07-31) — Analytics 3-tab IA. Prior 4-entry
  // block (Analytics / Spend / Activity / Insights) collapsed to 3
  // (Overview / Reports / Activity). "Insights" + "Spend" legacy
  // keywords still route to the new homes so Cmd-K muscle memory works.
  { id: 'page-admin-analytics', title: 'Analytics', subtitle: 'Dashboard — spend, activity, top drivers, callouts', kind: 'activity', groupLabel: 'Pages', to: '/admin/analytics', roles: ['owner', 'admin'], keywords: ['analytics', 'insights', 'overview', 'summary', 'dashboard', 'callouts', 'optimization', 'top users', 'top models'] },
  { id: 'page-admin-analytics-reports', title: 'Reports (analytics)', subtitle: 'Drilldown pivots — by user, capability, tag, day', kind: 'activity', groupLabel: 'Pages', to: '/admin/analytics/reports', roles: ['owner', 'admin'], keywords: ['reports', 'spend', 'cost', 'credits', 'by user', 'by model', 'by connector', 'by tag', 'by day', 'pivot', 'drilldown', 'csv', 'export'] },
  { id: 'page-admin-activity', title: 'Activity', subtitle: 'Raw event log — dispatches + admin audit', kind: 'activity', groupLabel: 'Pages', to: '/admin/analytics/activity', roles: ['owner', 'admin'], keywords: ['history', 'audit', 'runs', 'dispatches', 'event log'] },
  { id: 'page-admin-usage', title: 'Usage', subtitle: 'Team pot, monthly cap, auto-reload', kind: 'activity', groupLabel: 'Pages', to: '/admin/workspace/usage', roles: ['owner', 'admin'], keywords: ['usage', 'team pot', 'credits', 'balance', 'cap', 'limit', 'auto-reload', 'top up', 'buy credits'] },
  { id: 'page-admin-billing', title: 'Billing', subtitle: 'Payment method + invoices', kind: 'activity', groupLabel: 'Pages', to: '/admin/workspace/billing', roles: ['owner', 'admin'], keywords: ['payments', 'invoices', 'stripe', 'card', 'payment method', 'receipts'] },
  { id: 'page-admin-workspace', title: 'Workspace', subtitle: 'General settings, usage, billing, notifications', kind: 'connector', groupLabel: 'Pages', to: '/admin/workspace', roles: ['owner', 'admin'], keywords: ['setup', 'workspace', 'notifications', 'general', 'preferences'] },

  // --- Actions (verb-shaped commands) ----------------------------------
  { id: 'action-buy-credits', title: 'Buy credits', subtitle: 'Top up your team pot with a credit pack', kind: 'activity', groupLabel: 'Actions', action: 'buy_credits', keywords: ['topup', 'top up', 'purchase', 'balance', 'pay'] },
  { id: 'action-toggle-admin', title: 'Switch to admin mode', subtitle: 'Open workspace administration', kind: 'activity', groupLabel: 'Actions', action: 'toggle_admin', roles: ['owner', 'admin'], keywords: ['gear', 'toggle', 'switch'] },
  { id: 'action-invite-teammate', title: 'Invite a teammate', subtitle: 'Send someone an invitation', kind: 'user', groupLabel: 'Actions', to: '/admin/people?tab=invitations', roles: ['owner', 'admin'], keywords: ['invite', 'add user', 'new user'] },
  { id: 'action-install-connector', title: 'Install a connector', subtitle: 'Browse the connector catalog', kind: 'catalog_connector', groupLabel: 'Actions', to: '/market', keywords: ['add', 'install', 'connector', 'app', 'integration'] },
  // Push 5-post-smoke-28 dropped 'action-keyboard-help' row.
  { id: 'action-sign-out', title: 'Sign out', subtitle: 'End your session', kind: 'user', groupLabel: 'Actions', action: 'sign_out', keywords: ['logout', 'log out', 'exit', 'quit'] },
];

/** The palette commands `role` sees: an entry with `roles` only for those roles. */
export function paletteCommandsFor(role: Role): PaletteCommand[] {
  return PALETTE_COMMANDS.filter((cmd) => cmd.roles === undefined || cmd.roles.includes(role));
}
